/**
 * BroadcastBanner - the admin announcement strip under the Vera header.
 *
 * Mounted in BOTH shells. AppShell wraps the 12 modules; the launcher and
 * /admin use vera-shell's ShellLayout and are mounted outside it, so a banner
 * added to one is invisible on the other, and the launcher is where people
 * land.
 *
 * Renders ONE strip at a time (critical, then warning, then info; newest start
 * first inside a tier), with a count chip that expands the rest. A 48px header
 * plus a Dev Data pill plus an impersonation strip is already a lot of
 * furniture above the content.
 *
 * Suppressed entirely while impersonating: you are inspecting someone else's
 * view, not standing in the audience, and that strip is already on screen.
 *
 * Design of record: docs/design/broadcast/PLAN.md
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import {
  DismissRegular,
  InfoRegular,
  WarningRegular,
  ErrorCircleRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../../../providers/AuthProvider';
import { veracityColors } from '../../../ui';
import {
  useBroadcasts,
  useDismissBroadcast,
  type BroadcastNotice,
  type BroadcastSeverity,
} from '../model/useBroadcasts';

const useStyles = makeStyles({
  strip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '6px 16px',
    fontSize: '13px',
    fontWeight: '500',
    flexShrink: 0,
    // The banner is chrome, not content: it has no place on a printed
    // certificate or plan packet.
    '@media print': {
      display: 'none',
    },
  },
  // Severity maps to the OneVeracity secondary palette, not Fluent defaults.
  // Foregrounds are chosen per background for contrast, not fixed.
  info: {
    backgroundColor: veracityColors.duskBlue,
    color: veracityColors.firstLight,
  },
  warning: {
    backgroundColor: veracityColors.daybreakAmber,
    color: veracityColors.midnightCharcoal,
  },
  critical: {
    backgroundColor: veracityColors.morningCoral,
    color: veracityColors.midnightCharcoal,
  },
  message: {
    // Long messages truncate rather than growing the header downward. The
    // server caps at 280 chars; this handles narrow viewports.
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '70vw',
  },
  action: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    backgroundColor: 'rgba(255,255,255,0.22)',
    border: '1px solid rgba(255,255,255,0.45)',
    borderRadius: '4px',
    color: 'inherit',
    fontSize: '12px',
    fontWeight: '600',
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.38)',
    },
  },
  onLight: {
    // The amber and coral strips need a darker scrim than the blue one.
    // Longhands, not `borderColor`: Griffel rejects that shorthand.
    backgroundColor: 'rgba(0,0,0,0.10)',
    borderTopColor: 'rgba(0,0,0,0.28)',
    borderRightColor: 'rgba(0,0,0,0.28)',
    borderBottomColor: 'rgba(0,0,0,0.28)',
    borderLeftColor: 'rgba(0,0,0,0.28)',
    ':hover': {
      backgroundColor: 'rgba(0,0,0,0.18)',
    },
  },
  iconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  dismiss: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    padding: '0',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: 'inherit',
    cursor: 'pointer',
    flexShrink: 0,
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.25)',
    },
  },
  more: {
    padding: '1px 8px',
    backgroundColor: 'rgba(255,255,255,0.22)',
    border: 'none',
    borderRadius: '10px',
    color: 'inherit',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer',
    flexShrink: 0,
  },
  moreOnLight: {
    backgroundColor: 'rgba(0,0,0,0.14)',
  },
});

const ICONS: Record<BroadcastSeverity, typeof InfoRegular> = {
  info: InfoRegular,
  warning: WarningRegular,
  critical: ErrorCircleRegular,
};

/** Amber and coral carry dark text, so their overlays need dark scrims. */
function isLightStrip(severity: BroadcastSeverity): boolean {
  return severity !== 'info';
}

function BroadcastStrip({
  notice,
  extraCount,
  onToggleMore,
}: {
  notice: BroadcastNotice;
  extraCount: number;
  onToggleMore?: () => void;
}) {
  const styles = useStyles();
  const dismissMutation = useDismissBroadcast();
  const Icon = ICONS[notice.severity];
  const light = isLightStrip(notice.severity);
  const isInternal = notice.linkUrl?.startsWith('/') ?? false;

  return (
    <div
      className={mergeClasses(styles.strip, styles[notice.severity])}
      // Critical interrupts a screen reader; info and warning wait their turn.
      role={notice.severity === 'critical' ? 'alert' : 'status'}
    >
      <span className={styles.iconWrap}>
        <Icon fontSize={16} />
      </span>

      <span className={styles.message} title={notice.message}>
        {notice.message}
      </span>

      {notice.linkUrl &&
        (isInternal ? (
          <Link
            to={notice.linkUrl}
            className={mergeClasses(styles.action, light && styles.onLight)}
          >
            {notice.linkLabel || 'Open'}
          </Link>
        ) : (
          <a
            href={notice.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={mergeClasses(styles.action, light && styles.onLight)}
          >
            {notice.linkLabel || 'Open'}
          </a>
        ))}

      {extraCount > 0 && (
        <button
          type="button"
          className={mergeClasses(styles.more, light && styles.moreOnLight)}
          onClick={onToggleMore}
          aria-label={`Show ${extraCount} more announcement${extraCount === 1 ? '' : 's'}`}
        >
          +{extraCount}
        </button>
      )}

      {notice.dismissible && (
        <button
          type="button"
          className={styles.dismiss}
          onClick={() => dismissMutation.mutate(notice.id)}
          aria-label="Dismiss announcement"
          title="Dismiss"
        >
          <DismissRegular fontSize={14} />
        </button>
      )}
    </div>
  );
}

export function BroadcastBanner() {
  const { impersonation } = useAuth();
  const { data } = useBroadcasts();
  const [expanded, setExpanded] = useState(false);

  // Inspecting someone else's view, not standing in the audience.
  if (impersonation) return null;

  // Only the undismissed ones. Anything dismissed is still live and still
  // reachable, from the header bell.
  const notices = data?.notices ?? [];
  if (notices.length === 0) return null;

  const visible = expanded ? notices : notices.slice(0, 1);

  return (
    <>
      {visible.map((notice, index) => (
        <BroadcastStrip
          key={notice.id}
          notice={notice}
          extraCount={index === 0 && !expanded ? notices.length - 1 : 0}
          onToggleMore={() => setExpanded(true)}
        />
      ))}
    </>
  );
}

export default BroadcastBanner;
